import React, { useState } from 'react';
import { CheckIcon, LockIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { TextField } from '../components/forms/TextField';
import { CheckboxField } from '../components/forms/CheckboxField';
import { ProductVisual } from '../components/ProductVisual';
import { SweepLoader } from '../components/SweepLoader';
import { NeedFromAbby } from '../components/NeedFromAbby';
import { useCart } from '../contexts/CartContext';
import { usePageMeta } from '../hooks/usePageMeta';
import { getProductPrice, products } from '../data/products';
import { formatPrice } from '../utils/currency';
import { isValidEmail } from '../utils/validation';

/** Checkout UI only. [NEED FROM ABBY] To be connected to Abby's 1Dash account. */
export function Checkout() {
  usePageMeta('Checkout', 'Complete your True Twelve order.');
  const {
    lines,
    clear
  } = useCart();
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const items = lines.flatMap(line => {
    const product = products.find(p => p.id === line.productId);
    return product ? [{
      line,
      product,
      total: getProductPrice(product, line.option) * line.qty
    }] : [];
  });
  const subtotal = items.reduce((s, i) => s + i.total, 0);
  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const d = new FormData(e.currentTarget);
    const get = (k: string) => String(d.get(k) ?? '').trim();
    const next: Record<string, string> = {};
    if (!isValidEmail(get('email'))) next.email = 'Enter a valid email address.';
    ['ship_name', 'ship_address', 'ship_city', 'ship_postal'].forEach(k => {
      if (!get(k)) next[k] = 'Required.';
    });
    setErrors(next);
    const first = Object.keys(next)[0];
    if (first) {
      document.getElementById(`f-${first}`)?.focus();
      return;
    }
    setStatus('submitting');
    window.setTimeout(() => {
      setStatus('success');
      clear();
    }, 1200);
  }
  if (status === 'success') {
    return <section className="gutter section mx-auto text-center" role="status" data-id="element-600" data-mp="31prwuqc2mkfg">
        <span className="mx-auto grid h-14 w-14 place-items-center rounded-full border border-gold" data-id="element-601" data-mp="3fp1abi8w70fq">
          <CheckIcon aria-hidden="true" strokeWidth={1.25} className="h-6 w-6 text-charcoal" data-id="element-602" data-mp="3apx14rlyx2pe" />
        </span>
        <h1 className="t-h2 mx-auto mt-8 max-w-[18ch]" data-id="element-603" data-mp="2n02pgdddxk6q">Thank you. Your order is in.</h1>
        <p className="t-lead mx-auto mt-4 max-w-[44ch] text-graphite" data-id="element-604" data-mp="2iyr26ehzhb1p">A confirmation will arrive by email shortly.</p>
        <Button to="/readings?type=birth-chart#book" variant="outline" className="mt-10" data-id="element-605" data-mp="2wqp4a5da26od">
          Pair it with a Birth Chart Reading
        </Button>
      </section>;
  }
  if (items.length === 0) {
    return <section className="gutter section text-center" data-id="element-606" data-mp="248t7rgtgnp42">
        <h1 className="t-h2" data-id="element-607" data-mp="1zarlyo4nhbow">Your cart is empty.</h1>
        <p className="mt-4 text-graphite" data-id="element-608" data-mp="1za00450uusbt">Find something for every hour.</p>
        <Button to="/shop" className="mt-8" data-id="element-609" data-mp="esh36wn2hnnq">
          Explore the Shop
        </Button>
      </section>;
  }
  return <section aria-labelledby="checkout-title" className="gutter grid gap-[clamp(2.5rem,5vw,7rem)] py-[clamp(3rem,6vw,7rem)] lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]" data-id="element-610" data-mp="jl0hxwxhqou4">
      <div data-id="element-611" data-mp="aqcfeejavl90">
        <h1 id="checkout-title" className="t-h2" data-id="element-612" data-mp="as6x7jz9yx8v">
          Checkout
        </h1>
        <NeedFromAbby className="mt-4" data-id="element-613" data-mp="ygggof90n8wu">1Dash payment connection</NeedFromAbby>
        <form onSubmit={handleSubmit} noValidate className="mt-10 space-y-12" aria-label="Checkout" data-id="element-614" data-mp="12i1p9oeip118">
          <fieldset data-id="element-615" data-mp="oiskdkvrhtck">
            <legend className="t-label mb-5 text-charcoal" data-id="element-616" data-mp="ojbre6jvixbt">Contact</legend>
            <TextField label="Email" name="email" type="email" autoComplete="email" error={errors.email} data-id="element-617" data-mp="thd9op9h6sm8" />
            <CheckboxField name="marketing_consent" className="mt-4" data-id="element-618" data-mp="39887o5vruzxg">
              Email me The Twelve Letter and first access to new pieces.
            </CheckboxField>
          </fieldset>
          <fieldset data-id="element-619" data-mp="3th94aa5i9th2">
            <legend className="t-label mb-5 text-charcoal" data-id="element-620" data-mp="3thr7jqql57yh">Shipping</legend>
            <div className="grid gap-5 md:grid-cols-2" data-id="element-621" data-mp="3or14wl9jnj7h">
              <TextField label="Full name" name="ship_name" autoComplete="name" error={errors.ship_name} className="md:col-span-2" data-id="element-622" data-mp="3ophn2q88p03l" />
              <TextField label="Address" name="ship_address" autoComplete="street-address" error={errors.ship_address} className="md:col-span-2" data-id="element-623" data-mp="3fq2zbyjgj3ms" />
              <TextField label="City" name="ship_city" autoComplete="address-level2" error={errors.ship_city} data-id="element-624" data-mp="g9o6ychtlqn2" />
              <TextField label="Postal code" name="ship_postal" autoComplete="postal-code" error={errors.ship_postal} data-id="element-625" data-mp="c9vsxk9nqsab" />
              <TextField label="State / Region" name="ship_region" autoComplete="address-level1" data-id="element-626" data-mp="7cdu4mpcttg1" />
              <TextField label="Country" name="ship_country" autoComplete="country-name" defaultValue="United States" data-id="element-627" data-mp="2danixtq7f5y" />
            </div>
          </fieldset>
          <fieldset data-id="element-628" data-mp="2n0dtn7vsyfeu">
            <legend className="t-label mb-5 flex items-center gap-2 text-charcoal" data-id="element-629" data-mp="2mzueu01g8751">
              <LockIcon aria-hidden="true" strokeWidth={1.25} className="h-3.5 w-3.5" data-id="element-630" data-mp="2cytdo8dqdx24" /> Payment
            </legend>
            <div className="border border-dashed border-[#8F877F] bg-cream p-6 text-sm text-graphite" data-id="element-631" data-mp="2hwux4uf2jye4">
              Secure card payment will appear here once checkout is connected. No payment is taken in this preview.
            </div>
          </fieldset>
          <Button type="submit" size="lg" disabled={status === 'submitting'} className="w-full sm:w-auto" data-id="element-632" data-mp="2vohxb5c4tr26">
            {status === 'submitting' ? <>
                <SweepLoader label="Placing order" data-id="element-633" data-mp="30ow1gcam4gux" /> Placing order
              </> : `Place Order · ${formatPrice(subtotal)}`}
          </Button>
        </form>
      </div>

      <aside aria-label="Order summary" className="bg-cream p-[clamp(1.5rem,3vw,3rem)] lg:sticky lg:top-28 lg:self-start" data-id="element-634" data-mp="1b8eievreseow">
        <h2 className="t-label text-charcoal" data-id="element-635" data-mp="1g6g4zqo1mw54">Order Summary</h2>
        <ul className="mt-6 divide-y divide-stone" data-id="element-636" data-mp="186juapu2j7v2">
          {items.map(({
          line,
          product,
          total
        }) => <li key={line.key} className="grid grid-cols-[4.5rem_minmax(0,1fr)_auto] items-center gap-4 py-4" data-id="element-637" data-mp="137p06rqnhwvn">
              <ProductVisual product={product} className="aspect-square" data-id="element-638" data-mp="1i592fqw5xdy4" />
              <div data-id="element-639" data-mp="1d5moff8ifto9">
                <p className="text-sm text-charcoal" data-id="element-640" data-mp="1d53ikww4wvql">{product.name}</p>
                <p className="text-sm text-graphite" data-id="element-641" data-mp="1d7zdsdgll6bc">
                  {line.option ? `${line.option} · ` : ''}Qty {line.qty}
                </p>
              </div>
              <p className="text-sm text-charcoal" data-id="element-642" data-mp="1m5rvfo9skq1w">{formatPrice(total)}</p>
            </li>)}
        </ul>
        <dl className="mt-4 space-y-2 border-t border-stone pt-4 text-sm" data-id="element-643" data-mp="20ufqzujn0zru">
          <div className="flex justify-between" data-id="element-644" data-mp="1vxffh440r4tc">
            <dt data-id="element-645" data-mp="29vvpb3x6hbfp">Subtotal</dt>
            <dd className="text-charcoal" data-id="element-646" data-mp="29v450p91704q">{formatPrice(subtotal)}</dd>
          </div>
          <div className="flex justify-between" data-id="element-647" data-mp="25utx371ohzn8">
            <dt data-id="element-648" data-mp="25w51n92y7bfe">Shipping</dt>
            <dd data-id="element-649" data-mp="2oikwlrxo8swn">Calculated next</dd>
          </div>
          <div className="flex justify-between border-t border-stone pt-3 text-base" data-id="element-650" data-mp="2ojvyfm7k3ql4">
            <dt className="text-charcoal" data-id="element-651" data-mp="2tgme48fg8k60">Total</dt>
            <dd className="text-charcoal" data-id="element-652" data-mp="2tfbb4attvgfy">{formatPrice(subtotal)}</dd>
          </div>
        </dl>
      </aside>
    </section>;
}